import { useState, useEffect, useRef, forwardRef } from 'react'
import type { FixtureLite, FixtureEvent } from '../types'
import { fmtKickoff } from '../utils'
import { FormBadge } from './FormBadge'
import { OddsBadge } from './OddsDisplay'
import { useOdds } from '../context/OddsContext'
import { PlayerProfileModal } from './PlayerProfileModal'

interface FixtureCardProps {
  fixture: FixtureLite
  isSelected: boolean
  onSelect: ((fixture: FixtureLite) => void) | (() => void)
  /** If true, scroll this card into view when selected */
  scrollIntoView?: boolean
}

// Format the status display for live/finished matches
function getStatusDisplay(fixture: FixtureLite): { label: string; extra: string | null; isLive: boolean; isPostponed: boolean } {
  const { status, status_detail, elapsed, elapsed_extra } = fixture
  const extra = elapsed_extra && elapsed_extra > 0 ? `+${elapsed_extra}` : null

  if (status === 'LIVE') {
    // Show detailed status for live matches
    if (status_detail === 'HT') {
      return { label: 'HT', extra: null, isLive: true, isPostponed: false }
    }
    if (status_detail === '1H' || status_detail === '2H') {
      if (elapsed) {
        return { label: `${elapsed}'`, extra, isLive: true, isPostponed: false }
      }
      // Fallback if elapsed not available
      return { label: status_detail === '1H' ? '1H' : '2H', extra: null, isLive: true, isPostponed: false }
    }
    if (status_detail === 'ET') {
      if (elapsed) {
        return { label: `ET ${elapsed}'`, extra, isLive: true, isPostponed: false }
      }
      return { label: 'ET', extra: null, isLive: true, isPostponed: false }
    }
    return { label: elapsed ? `${elapsed}'` : 'LIVE', extra, isLive: true, isPostponed: false }
  }

  if (status === 'FT') {
    return { label: 'FT', extra: null, isLive: false, isPostponed: false }
  }

  if (status === 'PST') {
    return { label: 'Postponed', extra: null, isLive: false, isPostponed: true }
  }

  // Not started - show kickoff time
  return { label: fmtKickoff(fixture.kickoff_utc), extra: null, isLive: false, isPostponed: false }
}

// Get goals for a specific team
function getTeamGoals(events: FixtureEvent[], teamId: number): FixtureEvent[] {
  return events.filter(
    (e) => e.event_type === 'Goal' && e.team_id === teamId
  )
}

// Format minute with optional extra time
function formatMinute(elapsed: number, elapsed_extra: number | null | undefined, detail: string | null | undefined): string {
  const minute = elapsed_extra
    ? `${elapsed}+${elapsed_extra}'`
    : `${elapsed}'`

  if (detail === 'Own Goal') return `OG ${minute}`
  if (detail === 'Penalty') return `P ${minute}`
  return minute
}

type ScorerDisplay = {
  playerId: number | null
  playerName: string
  minutes: string
  disallowed: boolean
  reason?: string // e.g., "offside", "Foul"
}

// Group goals by player and format as "Player 10', 45', 90'"
// Disallowed goals are returned separately with disallowed flag
// Now preserves player_id for clickable names
function formatGroupedScorers(goals: FixtureEvent[]): ScorerDisplay[] {
  // Separate valid and disallowed goals
  const validGoals = goals.filter((g) => !g.comments?.startsWith('VAR:'))
  const disallowedGoals = goals.filter((g) => g.comments?.startsWith('VAR:'))

  const results: ScorerDisplay[] = []

  // Group valid goals by player (using player_id as key if available, otherwise name)
  const playerGoalsMap = new Map<string, { playerId: number | null; playerName: string; minutes: string[] }>()
  validGoals.forEach((g) => {
    const playerName = g.player_name || 'Unknown'
    const playerId = g.player_id || null
    const minute = formatMinute(g.elapsed, g.elapsed_extra, g.detail)
    // Use player_id as key if available, otherwise use name
    const key = playerId ? `id_${playerId}` : `name_${playerName}`

    if (!playerGoalsMap.has(key)) {
      playerGoalsMap.set(key, { playerId, playerName, minutes: [] })
    }
    playerGoalsMap.get(key)!.minutes.push(minute)
  })

  // Add valid goals
  playerGoalsMap.forEach((data) => {
    results.push({
      playerId: data.playerId,
      playerName: data.playerName,
      minutes: data.minutes.join(', '),
      disallowed: false,
    })
  })

  // Add disallowed goals individually
  disallowedGoals.forEach((g) => {
    const playerName = g.player_name || 'Unknown'
    const playerId = g.player_id || null
    const minute = formatMinute(g.elapsed, g.elapsed_extra, g.detail)
    // Extract reason from comments like "VAR: Goal Disallowed - offside"
    const reason = g.comments?.replace('VAR: Goal Disallowed - ', '').replace('VAR: Goal cancelled', 'cancelled')
    results.push({
      playerId,
      playerName,
      minutes: minute,
      disallowed: true,
      reason,
    })
  })

  return results
}

export function FixtureCard({ fixture, isSelected, onSelect, scrollIntoView: shouldScroll }: FixtureCardProps) {
  const [statsExpanded, setStatsExpanded] = useState(false)
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null)
  const { oddsHidden } = useOdds()
  const cardRef = useRef<HTMLDivElement>(null)

  // Scroll into view when selected and scrollIntoView prop is true
  useEffect(() => {
    if (isSelected && shouldScroll && cardRef.current) {
      // Small delay to ensure the DOM is ready
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100)
    }
  }, [isSelected, shouldScroll])

  const handleClick = () => {
    // Support both callback signatures
    if (onSelect.length === 0) {
      (onSelect as () => void)()
    } else {
      (onSelect as (f: FixtureLite) => void)(fixture)
    }
  }

  const handleStatsToggle = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering fixture selection
    setStatsExpanded(!statsExpanded)
  }

  const statusDisplay = getStatusDisplay(fixture)
  const isLiveOrFinished = fixture.status === 'LIVE' || fixture.status === 'FT'
  const hasScore = fixture.home_score !== null && fixture.away_score !== null
  const isUpcoming = fixture.status === 'NS'
  const showOdds = isUpcoming && !oddsHidden

  // Get goals for each team
  const homeGoals = getTeamGoals(fixture.events || [], fixture.home_team.id)
  const awayGoals = getTeamGoals(fixture.events || [], fixture.away_team.id)

  // Check if we have live stats to display
  const hasLiveStats = fixture.home_stats || fixture.away_stats

  return (
    <div
      ref={cardRef}
      className={`card fixture-card ${isSelected ? 'fixture-card-selected' : ''} ${statusDisplay.isLive ? 'fixture-live' : ''} ${statusDisplay.isPostponed ? 'fixture-postponed' : ''}`}
      onClick={handleClick}
    >
      {fixture.venue && (
        <div className="fixture-header">
          <span className="fixture-venue-inline">{fixture.venue}</span>
        </div>
      )}
      <div className="fixture-matchup">
        <div className="fixture-team home">
          <div className="team-info">
            <span className="team-name">{fixture.home_team.name}</span>
            <FormBadge form={fixture.home_team.form} maxResults={5} size="sm" />
          </div>
          <span className="badge">
            {fixture.home_team.crest_url ? <img src={fixture.home_team.crest_url} alt="" /> : 'H'}
          </span>
        </div>
        <span className="fixture-vs-mobile">
          {isLiveOrFinished && hasScore ? `${fixture.home_score} - ${fixture.away_score}` : 'vs'}
        </span>
        <div className="fixture-center">
          <span className={`fixture-status ${statusDisplay.isLive ? 'status-live' : ''} ${statusDisplay.isPostponed ? 'status-postponed' : ''}`}>
            {statusDisplay.label}
            {statusDisplay.extra && <span className="elapsed-extra">{statusDisplay.extra}</span>}
          </span>
          <div className="fixture-score">
            {isLiveOrFinished && hasScore ? (
              <span>{fixture.home_score} - {fixture.away_score}</span>
            ) : (
              <span className="vs">vs</span>
            )}
          </div>
        </div>
        <div className="fixture-team away">
          <span className="badge">
            {fixture.away_team.crest_url ? <img src={fixture.away_team.crest_url} alt="" /> : 'A'}
          </span>
          <div className="team-info">
            <span className="team-name">{fixture.away_team.name}</span>
            <FormBadge form={fixture.away_team.form} maxResults={5} size="sm" />
          </div>
        </div>
      </div>
      {showOdds && (
        <div className="fixture-odds">
          <div className="odds-row">
            <span className="odds-label">1</span>
            <OddsBadge fixtureId={fixture.id} marketSlug="match-winner" outcome="home" size="sm" />
          </div>
          <div className="odds-row">
            <span className="odds-label">X</span>
            <OddsBadge fixtureId={fixture.id} marketSlug="match-winner" outcome="draw" size="sm" />
          </div>
          <div className="odds-row">
            <span className="odds-label">2</span>
            <OddsBadge fixtureId={fixture.id} marketSlug="match-winner" outcome="away" size="sm" />
          </div>
        </div>
      )}
      {fixture.referee_name && (
        <div className="fixture-meta">
          <span className="fixture-referee">
            Ref: {fixture.referee_name}
            {fixture.referee_strictness_label && fixture.referee_strictness_label !== 'average' && (
              <span className={`referee-strictness-tag ${fixture.referee_strictness_label}`}>
                {fixture.referee_strictness_label === 'strict' ? 'Strict' : 'Lenient'}
              </span>
            )}
          </span>
        </div>
      )}
      {(homeGoals.length > 0 || awayGoals.length > 0) && (
        <div className="fixture-scorers">
          <div className="scorers-col home">
            {formatGroupedScorers(homeGoals).map((scorer, i) => (
              <span
                key={i}
                className={`scorer ${scorer.disallowed ? 'disallowed' : ''}`}
                title={scorer.disallowed ? `VAR: ${scorer.reason}` : undefined}
              >
                {scorer.playerId ? (
                  <span
                    className="scorer-name clickable"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedPlayerId(scorer.playerId)
                    }}
                  >
                    {scorer.playerName}
                  </span>
                ) : (
                  <span className="scorer-name">{scorer.playerName}</span>
                )}{' '}
                {scorer.minutes}
                {scorer.disallowed && <span className="var-badge">VAR - {scorer.reason}</span>}
              </span>
            ))}
          </div>
          <div className="scorers-col away">
            {formatGroupedScorers(awayGoals).map((scorer, i) => (
              <span
                key={i}
                className={`scorer ${scorer.disallowed ? 'disallowed' : ''}`}
                title={scorer.disallowed ? `VAR: ${scorer.reason}` : undefined}
              >
                {scorer.playerId ? (
                  <span
                    className="scorer-name clickable"
                    onClick={(e) => {
                      e.stopPropagation()
                      setSelectedPlayerId(scorer.playerId)
                    }}
                  >
                    {scorer.playerName}
                  </span>
                ) : (
                  <span className="scorer-name">{scorer.playerName}</span>
                )}{' '}
                {scorer.minutes}
                {scorer.disallowed && <span className="var-badge">VAR - {scorer.reason}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      {hasLiveStats && isLiveOrFinished && (
        <>
          <div className="live-stats">
            <div className="stat-row">
              <span className="stat-value">{fixture.home_stats?.possession ?? '-'}%</span>
              <span className="stat-label">Possession</span>
              <span className="stat-value">{fixture.away_stats?.possession ?? '-'}%</span>
            </div>
            <div className="stat-row">
              <span className="stat-value">{fixture.home_stats?.shots ?? '-'}</span>
              <span className="stat-label">Shots</span>
              <span className="stat-value">{fixture.away_stats?.shots ?? '-'}</span>
            </div>
            <div className="stat-row">
              <span className="stat-value">{fixture.home_stats?.shots_on_target ?? '-'}</span>
              <span className="stat-label">On Target</span>
              <span className="stat-value">{fixture.away_stats?.shots_on_target ?? '-'}</span>
            </div>
            {statsExpanded && (
              <>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.shots_off_target ?? '-'}</span>
                  <span className="stat-label">Off Target</span>
                  <span className="stat-value">{fixture.away_stats?.shots_off_target ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.blocked_shots ?? '-'}</span>
                  <span className="stat-label">Blocked</span>
                  <span className="stat-value">{fixture.away_stats?.blocked_shots ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.shots_inside_box ?? '-'}</span>
                  <span className="stat-label">Inside Box</span>
                  <span className="stat-value">{fixture.away_stats?.shots_inside_box ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.shots_outside_box ?? '-'}</span>
                  <span className="stat-label">Outside Box</span>
                  <span className="stat-value">{fixture.away_stats?.shots_outside_box ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.corners ?? '-'}</span>
                  <span className="stat-label">Corners</span>
                  <span className="stat-value">{fixture.away_stats?.corners ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.fouls ?? '-'}</span>
                  <span className="stat-label">Fouls</span>
                  <span className="stat-value">{fixture.away_stats?.fouls ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.offsides ?? '-'}</span>
                  <span className="stat-label">Offsides</span>
                  <span className="stat-value">{fixture.away_stats?.offsides ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.yellows ?? '-'}</span>
                  <span className="stat-label">Yellows</span>
                  <span className="stat-value">{fixture.away_stats?.yellows ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.reds ?? '-'}</span>
                  <span className="stat-label">Reds</span>
                  <span className="stat-value">{fixture.away_stats?.reds ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.saves ?? '-'}</span>
                  <span className="stat-label">Saves</span>
                  <span className="stat-value">{fixture.away_stats?.saves ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.passes ?? '-'}</span>
                  <span className="stat-label">Passes</span>
                  <span className="stat-value">{fixture.away_stats?.passes ?? '-'}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{fixture.home_stats?.passes_pct != null ? `${fixture.home_stats.passes_pct}%` : '-'}</span>
                  <span className="stat-label">Pass %</span>
                  <span className="stat-value">{fixture.away_stats?.passes_pct != null ? `${fixture.away_stats.passes_pct}%` : '-'}</span>
                </div>
              </>
            )}
          </div>
          <button className="stats-toggle" onClick={handleStatsToggle}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={statsExpanded ? 'expanded' : ''}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </>
      )}
      {/* Player Profile Modal */}
      {selectedPlayerId && (
        <PlayerProfileModal
          playerId={selectedPlayerId}
          onClose={() => setSelectedPlayerId(null)}
        />
      )}
    </div>
  )
}
